import { AdsManagementWrapper } from "./style/AdsManagementStyle";
import back from "../../assets/Images/icons/svgIcon/back-arrow.svg";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import API from "../../api/axios";
import { APP_URLS } from "../../api/url";
import { toast } from "react-toastify";
import { APIError, removeUnderscoreAndCapitalize } from "../../core/utils";
import { Switch } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import { confirmAlert } from "react-confirm-alert";
import info from "../../assets/Images/image/info-icon.png";

const InfoTexts = {
  third_party_ads_frequency:
    "This setting determines how frequently third-party ads are displayed by specifying the number of Cartobike ads that should appear between them. For example, if you set the value to 3, users will see three Cartobike ads before the next third-party ad appears.",
  first_ads_postioning:
    "This setting controls where the first third-party ad appears on all listing pages by specifying how many Cartobike ads should be shown before it. For example, if you set the value to 5, users will see five Cartobike ads before the first third-party ad is displayed.",
  show_ad_after_no_of_bids:
    "This setting determines how frequently third-party ads are displayed after the number of bids by user. For example, if you set the value to 3, users will place three bids before the third-party ad appears.",
  app_open_ad_frequency:
    "This setting determines the time interval between third-party ad displays by specifying the number of hours to wait after an ad is shown on opening the Cartobike app. For example, if you set the value to 4, a new third-party ad will appear every 4 hours.",
  auction_ad_visibility_cut_off_time:
    "This setting controls the time buffer before displaying a third-party ad . Specifically, it defines the number of minutes before the end of an auction during which the ad should no longer be shown. For instance, if the value is set to 10, the ad will be hidden during the final 10 minutes of the auction. This helps avoid distractions during the critical final bidding period",
  show_popular_auction_classic_ads_listing_postioning:
    "This setting determines the placement of popular auction ads within the classic ads listing. By specifying the number of classic ads that should appear before inserting popular auction ads section"
};

const AdsManagement = () => {
  const navigate = useNavigate();

  const [adsManagementPageList, _adsManagementPageList] = useState([]);
  const [loading, _loading] = useState(false);
  const [isSaving, _isSaving] = useState(false);

  useEffect(() => {
    getPages();
  }, []);

  const getPages = () => {
    _loading(true);
    API.get(APP_URLS.ADS_MANAGEMENT)
      .then((res) => {
        const resp = res.data;
        if (resp.success === true) {
          _loading(false);
          _adsManagementPageList(resp.data.ads_management);
        } else {
          toast.error(resp.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          _loading(false);
        }
      })
      .catch(function (error) {
        _loading(false);
        let error_message = APIError(error);
        toast.error(error_message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const handleChange = (key, index) => {
    _adsManagementPageList((prevState) => {
      const updatedList = [...prevState];
      updatedList[index][key] = !updatedList[index][key];
      return updatedList;
    });
  };

  const handleInputChange = (index, key, value) => {
    _adsManagementPageList((prevList) => {
      const updated = [...prevList];
      updated[index][key] = value;
      return updated;
    });
  };

  const toggleAll = (checked, index) => {
    _adsManagementPageList((prevState) => {
      return prevState.map((item, i) => {
        if (index === i) {
          return {
            ...item,
            ios: checked,
            android: checked,
            web: checked,
          };
        }
        return item;
      });
    });
  };

  const handleSave = () => {
    if (isSaving) return;

    const payload = {
      ads_management: {},
    };

    for (const page of adsManagementPageList) {
      const { page: pageName, ios, android, web } = page;

      // Input validation
      if (
        pageName === "third_party_ads_frequency" ||
        pageName === "first_ads_postioning" ||
        pageName === "show_popular_auction_classic_ads_listing_postioning"

      ) {
        const platforms = { ios, android, web };
        for (let [key, val] of Object.entries(platforms)) {
          const numVal = Number(val);
          const max = key === "web" ? 14 : 9;

          if (isNaN(numVal) || numVal < 1 || numVal > max) {
            toast.error(
              `${removeUnderscoreAndCapitalize(
                pageName
              )} ${key} value must be between 1 and ${max}`,
              { position: toast.POSITION.TOP_RIGHT }
            );
            return;
          }
        }
      }

      if (pageName === "app_open_ad_frequency") {
        const platforms = { ios, android, web };
        for (let [key, val] of Object.entries(platforms)) {
          const numHours = Number(val);
          const maxhours = 24;

          if (isNaN(numHours) || numHours < 0 || numHours > maxhours) {
            toast.error(
              `${removeUnderscoreAndCapitalize(
                pageName
              )} ${key} value must be between 0 and ${maxhours}`,
              { position: toast.POSITION.TOP_RIGHT }
            );
            return;
          }
        }
      }

      if (pageName === "show_ad_after_no_of_bids") {
        const platforms = { ios, android, web };
        for (let [key, val] of Object.entries(platforms)) {
          const numBids = Number(val);
          const maxBids = 127;

          if (isNaN(numBids) || numBids < 0 || numBids > maxBids) {
            toast.error(
              `${removeUnderscoreAndCapitalize(
                pageName
              )} ${key} value must be between 0 and ${maxBids}`,
              { position: toast.POSITION.TOP_RIGHT }
            );
            return;
          }
        }
      }

      if (pageName === "auction_ad_visibility_cut_off_time") {
        const platforms = { ios, android, web };
        for (let [key, val] of Object.entries(platforms)) {
          const numMins = Number(val);
          const maxMins = 127;

          if (isNaN(numMins) || numMins < 0 || numMins > maxMins) {
            toast.error(
              `${removeUnderscoreAndCapitalize(
                pageName
              )} ${key} value must be between 0 and ${maxMins}`,
              { position: toast.POSITION.TOP_RIGHT }
            );
            return;
          }
        }
      }

      payload.ads_management[pageName] = {
        ios: +ios,
        android: +android,
        web: +web,
      };
    }

    confirmAlert({
      title: "Confirm to save changes",
      message: "Are you sure you want to make these changes?",
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: "overlay-custom-class-name",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            _isSaving(true);
            API.patch(APP_URLS.ADS_MANAGEMENT, payload)
              .then((res) => {
                _isSaving(false);
                const resp = res.data;
                if (resp.success) {
                  toast.success(
                    "Ads Management settings updated successfully.",
                    {
                      position: toast.POSITION.TOP_RIGHT,
                    }
                  );
                } else {
                  toast.error(resp.message, {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                }
              })
              .catch((error) => {
                _isSaving(false);
                toast.error(APIError(error), {
                  position: toast.POSITION.TOP_RIGHT,
                });
              });
          },
        },
        { label: "No" },
      ],
    });
  };

  return (
    <AdsManagementWrapper>
      <div className="row mt-4">
        <div className="col-md-4 d-flex align-items-center">
          <img
            src={back}
            className="ms-4 backbtn"
            alt="back button"
            onClick={() => navigate(-1)}
          />
        </div>
        <div className="col-md-4">
          <div className="heading">
            <h1 className="text-uppercase">Ads Management</h1>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="d-flex align-items-center justify-content-end">
          <button
            className="btn"
            disabled={loading || isSaving}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-lg-8 offset-lg-2">
          <div className="table-container py-2">
            <table className="table">
              <thead className="mb-3">
                <tr className="header">
                  <th className="pageName">Page</th>
                  <th>{""}</th>
                  <th>IOS</th>
                  <th>Android</th>
                  <th>Web</th>
                </tr>
              </thead>
              {loading ? (
                <div className="d-flex justify-content-center loader">
                  <ThreeDots
                    height="150"
                    width="150"
                    radius="9"
                    color="#637df4"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClassName=""
                    visible={true}
                  />
                </div>
              ) : (
                <>
                  <tbody>
                    {adsManagementPageList &&
                      adsManagementPageList.map((pageList, index) => {
                        const isInputField =
                          pageList.page === "third_party_ads_frequency" ||
                          pageList.page === "first_ads_postioning" ||
                          pageList.page === "app_open_ad_frequency" ||
                          pageList.page === "show_ad_after_no_of_bids" ||
                          pageList.page === "show_popular_auction_classic_ads_listing_postioning" ||
                          pageList.page ===
                          "auction_ad_visibility_cut_off_time";

                        return (
                          <tr key={pageList.page}>
                            <td className="pageName">
                              {removeUnderscoreAndCapitalize(pageList.page)}
                              {isInputField ? (
                                <span title={InfoTexts[pageList.page]}>
                                  <img src={info} className="ms-1 backbtn" />
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                            <td className="toggle">
                              {isInputField ? (
                                <></>
                              ) : (
                                <Switch
                                  name="selectAll"
                                  checked={
                                    pageList.ios &&
                                    pageList.android &&
                                    pageList.web
                                  }
                                  onChange={(e) =>
                                    toggleAll(e.target.checked, index)
                                  }
                                />
                              )}
                            </td>
                            <td className="permissions">
                              {isInputField ? (
                                <input
                                  type="number"
                                  className="form-control"
                                  value={pageList.ios}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "ios",
                                      e.target.value
                                    )
                                  }
                                />
                              ) : (
                                <div className="round-check">
                                  <input
                                    type="checkbox"
                                    className={"checkbox-round"}
                                    checked={pageList.ios}
                                    name="ios"
                                    onChange={(e) =>
                                      handleChange(e.target.name, index)
                                    }
                                  />
                                </div>
                              )}
                            </td>
                            <td className="permissions">
                              {isInputField ? (
                                <input
                                  type="number"
                                  className="form-control"
                                  value={pageList.android}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "android",
                                      e.target.value
                                    )
                                  }
                                />
                              ) : (
                                <div className="round-check">
                                  <input
                                    type="checkbox"
                                    className={"checkbox-round"}
                                    checked={pageList.android}
                                    name="android"
                                    onChange={(e) =>
                                      handleChange(e.target.name, index)
                                    }
                                  />
                                </div>
                              )}
                            </td>
                            <td className="permissions">
                              {isInputField ? (
                                <input
                                  type="number"
                                  className="form-control"
                                  value={pageList.web}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "web",
                                      e.target.value
                                    )
                                  }
                                />
                              ) : (
                                <div className="round-check">
                                  <input
                                    type="checkbox"
                                    className={"checkbox-round"}
                                    checked={pageList.web}
                                    name="web"
                                    onChange={(e) =>
                                      handleChange(e.target.name, index)
                                    }
                                  />
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </>
              )}
            </table>
          </div>
        </div>
      </div>
    </AdsManagementWrapper>
  );
};
export default AdsManagement;
